import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AnonDataState,
  DashboardBannerState,
  ScheduleRosterState,
} from 'features/productGrowth/types';

import {
  transformAnonBusinessDetails,
  transformAnonTeamMembers,
} from './PostSignup/ProviderIntegration/utils';
import {
  createPayrollFirstPayDate,
  createUploadDocument,
  createUploadSchedule,
  fetchAnonCompanyData,
  fetchDashboardBanner,
  fetchScheduleRosterStatusCount,
  SLICE_NAME,
} from './actions';

interface ProductGrowthState {
  isPending: boolean;
  error: Record<string, string | null>;
  success: Record<string, string | null>;
  uploadDocumentIsPending: boolean;
  uploadDocumentError: Record<string, string | null>;
  uploadDocumentSuccess: Record<string, string | null>;
  uploadScheduleIsPending: boolean;
  uploadScheduleError: Record<string, string | null>;
  uploadScheduleSuccess: Record<string, string | null>;
  scheduleRoster: ScheduleRosterState;
  dashboardBanner: DashboardBannerState;
  anonData: AnonDataState;
  taskManagerCheckout?: boolean;
  payrollCheckout?: boolean;
}

export const anonDataInitialState: AnonDataState = {
  url: '',
  extractionProcessId: '',
  created: '',
  expiresAt: '',
  loading: true,
  error: false,
  company: undefined,
  team: undefined,
};

export const initialState: ProductGrowthState = {
  isPending: false,
  error: {},
  success: {},
  uploadDocumentIsPending: false,
  uploadDocumentError: {},
  uploadDocumentSuccess: {},
  dashboardBanner: {
    isPending: false,
    error: undefined,
    data: undefined,
  },
  scheduleRoster: {
    isPending: false,
    scheduleStatus: '',
    rosterStatus: '',
    scheduleCount: 0,
    rosterCount: 0,
    date: '',
    partialBannerDismissed: false,
    unsuccessfulBannerDismissed: false,
  },
  uploadScheduleIsPending: false,
  uploadScheduleError: {},
  uploadScheduleSuccess: {},
  anonData: anonDataInitialState,
  taskManagerCheckout: false,
  payrollCheckout: false,
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,

  reducers: {
    setAnonData(state, action: PayloadAction<Partial<AnonDataState>>) {
      // Merge incoming data with existing anonData to preserve nested defaults
      state.anonData = {
        ...state.anonData,
        ...action.payload,
      };
    },
    setTaskManagerCheckout(state, action: PayloadAction<boolean>) {
      state.taskManagerCheckout = action.payload;
    },
    setPayrollCheckout(state, action: PayloadAction<boolean>) {
      state.payrollCheckout = action.payload;
    },
    clearAnonFetchingState(state) {
      state.anonData = anonDataInitialState;
    },
    setScheduleRoster(state, action) {
      state.scheduleRoster.rosterStatus = action.payload.roster_status;
      state.scheduleRoster.scheduleStatus = action.payload.schedule_status;
      state.scheduleRoster.rosterCount = action.payload.roster_count;
      state.scheduleRoster.scheduleCount = action.payload.schedule_count;
      state.scheduleRoster.partialBannerDismissed =
        action.payload.partial_banner_dismissed;
      state.scheduleRoster.unsuccessfulBannerDismissed =
        action.payload.unsuccessful_banner_dismissed;
    },
  },
  extraReducers: builder => {
    builder.addCase(
      createPayrollFirstPayDate.pending,
      (state: ProductGrowthState) => {
        state.isPending = true;
        state.error = {};
      }
    );

    builder.addCase(createPayrollFirstPayDate.fulfilled, (state, action) => {
      state.isPending = false;
      state.success = { message: action.payload };
      state.error = {};
    });

    builder.addCase(
      createPayrollFirstPayDate.rejected,
      (state: ProductGrowthState, action) => {
        state.isPending = false;
        state.error = { message: action.payload as string };
        state.success = {};
      }
    );

    builder.addCase(createUploadDocument.pending, state => {
      state.uploadDocumentIsPending = true;
      state.uploadDocumentError = {};
    });

    builder.addCase(createUploadDocument.fulfilled, (state, action) => {
      state.uploadDocumentIsPending = false;
      state.uploadDocumentSuccess = { message: action.payload };
      state.uploadDocumentError = {};
    });

    builder.addCase(createUploadDocument.rejected, (state, action) => {
      state.uploadDocumentIsPending = false;
      state.uploadDocumentError = { message: action.payload as string };
      state.uploadDocumentSuccess = {};
    });

    builder.addCase(createUploadSchedule.pending, state => {
      state.uploadScheduleIsPending = true;
      state.uploadDocumentError = {};
    });

    builder.addCase(createUploadSchedule.fulfilled, (state, action) => {
      state.uploadScheduleIsPending = false;
      state.uploadScheduleSuccess = { message: action.payload };
      state.uploadScheduleError = {};
    });

    builder.addCase(createUploadSchedule.rejected, (state, action) => {
      state.uploadScheduleIsPending = false;
      state.uploadScheduleError = { message: action.payload as string };
      state.uploadScheduleSuccess = {};
    });

    builder.addCase(fetchScheduleRosterStatusCount.pending, state => {
      state.scheduleRoster.isPending = true;
    });

    builder.addCase(
      fetchScheduleRosterStatusCount.fulfilled,
      (state, action) => {
        state.scheduleRoster.isPending = false;
        state.scheduleRoster.rosterStatus = action.payload.roster_status;
        state.scheduleRoster.scheduleStatus = action.payload.schedule_status;
        state.scheduleRoster.rosterCount = action.payload.roster_count;
        state.scheduleRoster.scheduleCount = action.payload.schedule_count;
        state.scheduleRoster.date = action.payload.date;
        state.scheduleRoster.partialBannerDismissed =
          action.payload.partial_banner_dismissed;
        state.scheduleRoster.unsuccessfulBannerDismissed =
          action.payload.unsuccessful_banner_dismissed;
      }
    );

    builder.addCase(fetchScheduleRosterStatusCount.rejected, state => {
      state.scheduleRoster.isPending = false;
    });

    builder.addCase(fetchDashboardBanner.pending, state => {
      state.dashboardBanner.isPending = true;
      state.dashboardBanner.error = undefined;
      state.dashboardBanner.data = undefined;
    });

    builder.addCase(fetchDashboardBanner.fulfilled, (state, action) => {
      state.dashboardBanner.isPending = false;
      state.dashboardBanner.error = undefined;
      state.dashboardBanner.data = {
        bannerType: action.payload.bannerType,
        isDismissable: action.payload.isDismissable,
      };
    });

    builder.addCase(fetchDashboardBanner.rejected, (state, action) => {
      state.dashboardBanner.isPending = false;
      state.dashboardBanner.error = action.payload as string;
      state.dashboardBanner.data = undefined;
    });

    builder.addCase(fetchAnonCompanyData.pending, state => {
      state.anonData.loading = true;
      state.anonData.error = false;
      state.anonData.company = undefined;
      state.anonData.team = undefined;
    });
    builder.addCase(fetchAnonCompanyData.rejected, state => {
      state.anonData.loading = false;
      state.anonData.error = true;
      state.anonData.company = undefined;
      state.anonData.team = undefined;
    });
    builder.addCase(fetchAnonCompanyData.fulfilled, (state, action) => {
      state.anonData.loading = false;
      state.anonData.error = false;
      state.anonData.company = transformAnonBusinessDetails(action.payload);
      state.anonData.team = transformAnonTeamMembers(
        action.payload.employee_roster || []
      );
    });
  },
});

export const { actions, reducer } = slice;
export const { clearAnonFetchingState } = actions;
